import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useLayersChange from "../../../../hooks/useLayersChange";
import { isdeletedFieldName } from "../../../../utils/API";
import { ConfigContext } from "../../../../utils/ConfigContext";
import { getOpsColor, getVisibleFilters } from "../../../../utils/helper";
import { getLayerSymbology } from "../../../../utils/symbologies";
import FilterFieldCombo from "../../../Filter/FilterFieldCombo";
import WidgetButton from "../../../Header/components/WidgetButton";
import { ArrowLeftIcon, CloseIcon, FilterIcon } from "../../../Icons";
import { StyledSubmitButton } from "../../../Report/new/Footer/Footer-styled";
import { Button, IconButton } from "../../../UI";
import FilterSection from "../FilterSection";
import {
  StyledFiltersBackButton,
  StyledFiltersBody,
  StyledFiltersHeader,
} from "../MobileFilter-styled";

export const areObjectsEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      for (let i = 0; i < val1.length; i++) {
        if (val1[i] !== val2[i]) return false;
      }
    } else if (val1 !== val2) {
      return false;
    }
  }

  return true;
};

const Filters = ({
  setShowFilters,
  showFilters,
  initialFilters,
  menuPosition,
  onFilterApply,
  filters,
  setAndApplyFilters,
  setShowMessage,
  isMobile,
  fieldsValueCountMap = {},
  fieldsRelationsMap = {},
  fieldsLayers = {},
}) => {
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");
  let activeModule = useSelector((store) => store.activeModule);
  const [localFilters, setLocalFilters] = useState({});
  const { layerCount } = useLayersChange(config);

  const activeFilters = useMemo(() => {
    return Object.values(getVisibleFilters(config, activeModule, filters));
  }, [config, layerCount, filters, activeModule]);

  const configFilteredFields = useMemo(() => {
    const result = {};
    if (!config) return result;

    if (Array.isArray(config.filterFields)) {
      config.filterFields.forEach((f) => {
        result[f.name] = f;
      });
    }

    return result;
  }, [config]);

  useEffect(() => {
    const moduleFilters = filters[activeModule] || {};
    const newLocalFilters = { ...localFilters };

    Object.keys(moduleFilters).forEach((k) => {
      if (
        localFilters[k] === undefined ||
        localFilters[k] !== moduleFilters[k]
      ) {
        newLocalFilters[k] = moduleFilters[k];
      }
    });

    setLocalFilters(newLocalFilters);
  }, [filters]);

  const onFilterChange = (fieldName, value) => {
    let newFilters = JSON.parse(JSON.stringify(localFilters));
    newFilters[fieldName] = value;
    setLocalFilters(newFilters);
  };

  const handleReset = () => {
    let _filters = JSON.parse(JSON.stringify(filters));

    const moduleFilters = initialFilters[activeModule];
    _filters[activeModule] = moduleFilters;
    setLocalFilters(moduleFilters);
    setAndApplyFilters(_filters);
    if (onFilterApply) {
      onFilterApply();
    }
  };

  const handleApplyFilters = useCallback(() => {
    let _filters = JSON.parse(JSON.stringify(filters));
    _filters[activeModule] = localFilters;
    setAndApplyFilters(_filters);

    if (onFilterApply) {
      onFilterApply();
    }
    setShowFilters(false);
  }, [filters, localFilters, setAndApplyFilters]);

  const handleBack = useCallback(() => {
    const areFiltersEqual = areObjectsEqual(
      localFilters,
      filters[activeModule]
    );
    if (areFiltersEqual) {
      setShowFilters(false);
    } else {
      setShowMessage(true);
    }
  }, [localFilters, filters, activeModule]);

  const opsColor = useMemo(
    () => getOpsColor(config, activeModule),
    [activeModule, config]
  );

  const hasUnAppliedFilters = useMemo(() => {
    return !areObjectsEqual(localFilters, filters[activeModule] || {});
  }, [localFilters, filters, activeModule]);

  const sortedActiveFilters = useMemo(() => {
    return activeFilters.slice().sort((a, b) => {
      if (
        a.field.name === isdeletedFieldName &&
        b.field.name !== isdeletedFieldName
      )
        return 1;
      if (
        b.field.name === isdeletedFieldName &&
        a.field.name !== isdeletedFieldName
      )
        return -1;

      // Sort by allLayers length (largest to smallest)
      const aLen = Array.isArray(a.allLayers) ? a.allLayers.length : 0;
      const bLen = Array.isArray(b.allLayers) ? b.allLayers.length : 0;
      if (bLen !== aLen) return bLen - aLen;

      return 0;
    });
  }, [activeFilters]);

  return (
    <>
      <StyledFiltersHeader>
        {isMobile && (
          <StyledFiltersBackButton
            className="filter__arrow-left"
            onClick={handleBack}
          >
            <ArrowLeftIcon />
          </StyledFiltersBackButton>
        )}
        {!isMobile && <FilterIcon style={{ marginLeft: "auto" }} />}
        <span>{t("screen.widget.Filter.title")}</span>
        <div style={{ marginLeft: "auto", display: "flex" }}>
          {isMobile ? (
            <StyledFiltersBackButton
              style={{ padding: "0px 18px" }}
              onClick={handleReset}
            >
              <span style={{ color: "#000" }}>
                {t("screen.widget.Filter.reset")}
              </span>
            </StyledFiltersBackButton>
          ) : (
            <div
              style={{
                display: "flex",
                gap: "8px",
                position: "absolute",
                right: "10px",
                top: "10px",
              }}
            >
              <Button onClick={handleReset} isNew>
                {t("screen.widget.Filter.reset")}
              </Button>
              <IconButton onClick={handleBack}>
                <CloseIcon />
              </IconButton>
            </div>
          )}
          {isMobile && (
            <WidgetButton>
              <FilterIcon />
            </WidgetButton>
          )}
        </div>
      </StyledFiltersHeader>
      <StyledFiltersBody>
        {showFilters &&
          sortedActiveFilters.map((item) => {
            const { field, layer } = item;
            const fieldsMap = fieldsValueCountMap[field.name] || new Map();

            const symbology = getLayerSymbology(layer, config);

            const { colorMap = {} } = symbology || {};
            let colors = {};

            if (
              Array.isArray(colorMap.fields) &&
              colorMap.fields.includes(field.name)
            ) {
              colors = colorMap.default;
            } else if (colorMap.field === field.name) {
              colors = colorMap.default;
            }

            const defaultValue = localFilters[field.name] ?? [];

            return (
              <FilterSection
                key={field.name}
                title={t(
                  "layer.fieldAlias." + field.name + ".title",
                  field.alias
                )}
                appliedFilters={defaultValue.length}
                fieldsLayers={fieldsLayers[field.name] || []}
                t={t}
                config={config}
              >
                <FilterFieldCombo
                  key={`ff_${field.name}`}
                  field={field}
                  t={t}
                  referenceKey={field.name}
                  onValuesChange={onFilterChange.bind(this)}
                  defaultValue={defaultValue}
                  multi={true}
                  isMobile={true}
                  colors={colors}
                  menuPosition={menuPosition}
                  exclude={configFilteredFields[field.name]?.exclude || []}
                  showCount={configFilteredFields[field.name]?.showCount}
                  fieldsValueCountMap={
                    fieldsValueCountMap[field.name] || new Map()
                  }
                  fieldsRelationsMap={
                    fieldsRelationsMap[field.name] || new Map()
                  }
                />
              </FilterSection>
            );
          })}

        {isMobile ? (
          <StyledSubmitButton
            onClick={handleApplyFilters}
            bg={opsColor}
            style={{
              position: "absolute",
              bottom: "20px",
              margin: "auto",
              left: "50%",
              transform: "translateX(-50%)",
              padding: "0px 24px",
              fontSize: "16px",
            }}
          >
            {t("screen.widget.Filter.applyFilters")}
          </StyledSubmitButton>
        ) : null}
      </StyledFiltersBody>
      {!isMobile && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "8px 0px",
          }}
        >
          <Button
            onClick={handleApplyFilters}
            isNew
            selected={hasUnAppliedFilters}
          >
            {t("screen.widget.Filter.applyFilters")}
          </Button>
        </div>
      )}
    </>
  );
};

export default Filters;
